import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, CONTACT_INFO } from '../data/mock';
import { CheckCircle2, ArrowRight, ArrowUpRight, Truck, ShieldCheck, RotateCcw, Music2, Star } from 'lucide-react';

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'professional-concert-snare-drum') || products[0];
  const gridSlugs = [
    'full-size-viola',
    'handpan-steel-drum',
    'elite-concert-flute',
    'maestro-clarinet',
    'guitar-starter-set',
    'lyre-harp',
    'wood-color-record-player',
    'swan-harmonica-set',
  ];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => {
    document.title = 'Pitch Craft Gear | Quality Musical Instruments & Music Gear';
  }, []);

  return (
    <div className="min-h-screen bg-dark-50 text-dark-700">
      <Header />
      <CartDrawer />

      <main>
        {/* Hero — light, editorial split */}
        <section className="relative bg-dark-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="animate-fade-up">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-dark-200 text-athletic-700 text-xs font-medium uppercase tracking-[0.2em] rounded-full mb-7">
                  <Music2 className="w-3.5 h-3.5" /> Crafted for players
                </span>
                <h1 className="font-display text-[2.75rem] leading-[1.05] sm:text-6xl font-semibold text-dark-900 tracking-tight">
                  Find your <span className="text-athletic-600 italic">sound</span>, at your own pace
                </h1>
                <p className="mt-6 text-lg text-dark-600 max-w-xl leading-relaxed">
                  Pitch Craft Gear is a calm, hand-picked lineup of instruments and music gear for players at every stage &mdash; from first strum to sold-out show. Free USA shipping on everything.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-athletic-500 px-8 py-4 font-semibold tracking-wide text-white transition hover:bg-athletic-600 shadow-button">
                    Shop Instruments <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/shop?category=drums-percussion" className="inline-flex items-center gap-2 rounded-full border border-dark-900 px-8 py-4 font-semibold tracking-wide text-dark-900 hover:bg-dark-900 hover:text-white transition">
                    Explore Drums
                  </Link>
                </div>
                <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-dark-600">
                  <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-600" /> Free USA shipping</span>
                  <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-600" /> 30-day returns</span>
                  <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-athletic-600" /> Secure checkout</span>
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-[2rem] overflow-hidden shadow-elevated bg-white">
                  <img src={siteImages.heroMain} alt="Musical instruments in a warm studio" className="w-full h-[380px] sm:h-[480px] object-cover" />
                </div>
                <div className="hidden sm:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-card px-5 py-4 items-center gap-3 border border-dark-100">
                  <span className="flex items-center gap-0.5 text-elite-500">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  </span>
                  <div className="leading-tight">
                    <p className="text-sm font-semibold text-dark-900">Loved by players</p>
                    <p className="text-xs text-dark-500">Curated, playable gear</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category collection */}
        <section className="bg-white py-16 sm:py-20 border-t border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">The collection</span>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">Pick your instrument</h2>
              </div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide text-athletic-700 hover:gap-2.5 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative rounded-2xl overflow-hidden bg-dark-100 aspect-[4/3]"
                >
                  <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950/80 via-dark-950/25 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <p className="text-elite-300 text-[11px] font-medium uppercase tracking-[0.2em]">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                    <h3 className="font-display text-xl font-semibold text-white mt-1 flex items-center gap-1.5">
                      {category.name}
                      <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Best sellers grid */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">On the workbench</span>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">Players&rsquo; favorites</h2>
              <p className="mt-3 text-dark-600">A tight, honest lineup we would happily put in our own cases.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productsGrid.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-dark-900 text-dark-900 font-semibold tracking-wide rounded-full hover:bg-dark-900 hover:text-white transition">
                Browse all instruments <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Flagship spotlight — warm card */}
        <section className="bg-white py-16 sm:py-20 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-[2rem] overflow-hidden bg-dark-900 grid lg:grid-cols-2">
              <div className="relative min-h-[300px]">
                <img src={siteImages.ctaBackground} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-dark-900 via-dark-900/40 to-transparent" />
                <img src={featuredProduct.image} alt={featuredProduct.name} className="absolute inset-0 m-auto max-h-[70%] max-w-[70%] object-contain drop-shadow-2xl" />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-elite-400 mb-3">Flagship spotlight</span>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">{featuredProduct.name}</h2>
                <p className="text-dark-300 mt-4 mb-6 max-w-lg leading-relaxed">{featuredProduct.description.slice(0, 175)}&hellip;</p>
                <p className="text-3xl font-semibold text-elite-400 mb-7 font-display">${featuredProduct.price.toFixed(2)}</p>
                <div className="flex flex-wrap gap-3">
                  <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-500 text-white rounded-full font-semibold tracking-wide hover:bg-athletic-600 transition">Buy now</Link>
                  <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/25 text-white rounded-full font-medium tracking-wide hover:bg-white/10 transition">Browse more</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 tracking-tight">From cart to concert</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="relative bg-white border border-dark-100 rounded-2xl p-7 shadow-soft">
                  <span className="font-display text-5xl font-semibold text-athletic-500/25">0{step.id}</span>
                  <h3 className="font-display text-lg font-semibold text-dark-900 mt-2">{step.title}</h3>
                  <p className="text-dark-600 text-sm mt-2 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Pitch Craft */}
        <section className="bg-white py-16 sm:py-20 border-t border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
              <img className="h-80 w-full lg:h-[440px] lg:max-w-md rounded-[2rem] object-cover shadow-elevated flex-shrink-0" src={siteImages.heroLifestyle} alt="Inside the Pitch Craft Gear workshop" />
              <div>
                <span className="inline-block px-3 py-1 bg-athletic-50 text-athletic-700 text-xs font-semibold uppercase tracking-[0.2em] rounded-full mb-3">Why Pitch Craft</span>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-dark-900 tracking-tight">
                  Gear worth <span className="text-athletic-600 italic">playing</span>
                </h2>
                <p className="mt-5 text-lg text-dark-600 leading-relaxed">
                  We skip the endless walls of lookalike gear and hand-pick a small lineup we would actually put in our own cases &mdash; then back every one with free shipping and easy returns.
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {['Handpicked, playable stock', 'Clean intonation & setup', 'Beginner to gigging players', 'Real support, real people', 'Fast free USA shipping', 'Honest, upfront pricing'].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-dark-700">
                      <CheckCircle2 className="h-5 w-5 flex-none text-athletic-600 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/why-choose-us" className="mt-9 inline-flex items-center gap-2 text-athletic-700 font-semibold tracking-wide hover:gap-3 transition-all">
                  Learn more about us <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl font-semibold text-dark-900 text-center mb-10 tracking-tight">Frequently asked questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'What is the typical shipping time?', a: `Your order is processed and shipped via USPS within 1 business day. Delivery usually lands within 2 to 4 business days. For delays, contact us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping free?', a: 'Yes — every order ships with free USPS Standard Shipping within the USA, no minimum required.' },
                { q: 'How do returns work?', a: 'You have 30 days from delivery to start a return. Just reach out to our team and we will walk you through it.' },
                { q: 'How do I reach support?', a: `Our team is available at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-dark-100 shadow-soft">
                  <h3 className="font-display text-lg font-semibold text-dark-900">{faq.q}</h3>
                  <p className="mt-2 text-dark-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold tracking-wide">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
